// eslint-disable-next-line no-restricted-imports
import { useParams, useSearchParams } from 'react-router';

/**
 * Hook to retrieve a flag key from the current route, supporting both flags and AI configs.
 *
 * Use this when you need to get a flag key from the URL, but the route param might be named differently (e.g. 'flagKey' for flags, 'configKey' for AI configs).
 *
 * If you add another context/route param in the future, extend the FlagKeyContext type and add another branch to the hook logic.
 *
 * This will automatically check for both `flagKey` and `configKey` route parameters.
 * Returns the key and a context string indicating the source ("flag" or "ai-config").
 * Throws if neither is present in the route.
 *
 * Example:
 *   const { key, context } = usePolymorphicFlagKey();
 *   // context is 'flag' or 'ai-config'
 */
export function usePolymorphicFlagKey() {
  const params = useParams();
  const [searchParams] = useSearchParams();

  if (params.flagKey) {
    return { key: params.flagKey, context: 'flag' } as const;
  }

  if (params.configKey) {
    return { key: params.configKey, context: 'ai-config' } as const;
  }

  if (params.experimentKey) {
    const flagKeyFromSearch = searchParams.get('flagKey');
    return { key: flagKeyFromSearch ?? params.experimentKey, context: 'experiment' } as const;
  }

  throw new Error('Expected flagKey, configKey, or experimentKey in route');
}
